/**
 * Implement a class that represents a Groq client
 * and provides a set of tools for converting messages to and from the LLM.
 * - sendThread(thread: Thread): Thread - send a message to the LLM
 * - streamThread(thread: Thread): AsyncGenerator<string, Thread> - stream a message to the LLM
 */

import { nanoid } from "nanoid";
import Groq from "groq-sdk";
import { zodToJsonSchema } from "zod-to-json-schema";
import { Message, ToolCall } from "../message";
import { Thread } from "../thread";
import { Tool } from "../tool";
import { traceable } from "../tracing";
import { BaseModelDriver } from "./base";

// Constants
const MAX_PROMPT_CHARACTERS = 100_000;
const JSON_SCHEMA_URL = "http://json-schema.org/draft-07/schema#";



/**
 * Available Groq models
 */
export type GroqModel =
  | "llama3-8b-8192"
  | "llama3-70b-8192"
  | "llama3-8b"
  | "llama3-70b"
  | "mixtral-8x7b-32768"
  | "gemma-7b-it"
  | "gemma2-9b-it"

/**
 * Configuration options for the Groq driver
 */
export interface GroqConfig {
  /**
   * Groq API Key
   */
  apiKey?: string;

  /**
   * Model to use
   * @default "llama3-8b-8192"
   */
  model?: GroqModel | string;

  /**
   * Temperature for response generation
   * @default 0.7
   */
  temperature?: number;

  /**
   * Maximum number of tokens to generate
   */
  maxTokens?: number;

  /**
   * Whether to cache responses
   * @default false
   */
  cache?: boolean;
  
  /**
   * Whether to enable tracing
   * @default false
   */
  trace?: boolean;
  
  /**
   * Additional metadata for tracing
   */
  traceMetadata?: Record<string, unknown>;
  
  /**
   * Disable parallel tool calls
   * Some models don't support parallel tool calls
   * @default true
   */
  disableParallelToolCalls?: boolean;
}

/**
 * Represents a Groq client and provides tools for converting messages to/from the LLM
 */
export class GroqThreadDriver extends BaseModelDriver {
  /**
   * Groq API Client
   */
  private client: Groq;

  /**
   * Groq model to use
   */
  private model: GroqModel | string;

  /**
   * Temperature for response generation
   */
  private temperature: number;

  /**
   * Maximum number of tokens to generate
   */
  private maxTokens?: number;

  /**
   * Whether to cache responses
   */
  private cache: boolean;
  
  /**
   * Whether to disable parallel tool calls
   */
  private disableParallelToolCalls: boolean;

  /**
   * Returns a list of available Groq models
   */
  static getAvailableModels(): GroqModel[] {


    return [
      "llama3-8b-8192",
      "llama3-70b-8192",
      "llama3-8b",
      "llama3-70b",
      "mixtral-8x7b-32768",
      "gemma-7b-it",
      "gemma2-9b-it"
    ];
  }

  /**
   * Create a new Groq driver
   * @param config Configuration options
   */
  constructor(config: GroqConfig = {}) {
    super();

    // Get API Key from config or environment variable
    const apiKey = config.apiKey || process.env.GROQ_API_KEY;
    if (!apiKey) {
      throw new Error(
        "Groq API Key must be provided in config or as GROQ_API_KEY environment variable"
      );
    }

    // Create a base client
    const baseClient = new Groq({
      apiKey,
    });

    
    
    // Only wrap with tracing if trace is enabled (default to false)
    if (config.trace ?? false) {
      // Create a proxy around the Groq client to intercept API calls
      this.client = new Proxy(baseClient, {
        get: (target, prop, receiver) => {
          const value = Reflect.get(target, prop, receiver);
          
          // Handle nested properties (chat.completions.create)
          if (prop === 'chat' && value && typeof value === 'object') {
            return new Proxy(value, {
              get: (chatTarget, chatProp, chatReceiver) => {
                const chatValue = Reflect.get(chatTarget, chatProp, chatReceiver);
                
                // Handle completions property
                if (chatProp === 'completions' && chatValue && typeof chatValue === 'object') {
                  return new Proxy(chatValue, {
                    get: (completionsTarget, completionsProp, completionsReceiver) => {
                      const completionsValue = Reflect.get(completionsTarget, completionsProp, completionsReceiver);
                      
                      // Wrap the create method with tracing
                      if (completionsProp === 'create' && typeof completionsValue === 'function') {
                        return traceable(completionsValue.bind(completionsTarget), {
                          run_type: "llm",
                          name: "groq",
                          ...config.traceMetadata,
                          aggregator: (args: any[], result: any) => {
                            // Extract relevant data for tracing
                            const params = args[0] || {};
                            
                            // For streaming responses, we can't easily get metrics, so just return the input
                            if (params.stream) {
                              return {
                                model: params.model,
                                input: {
                                  messages: params.messages,
                                  temperature: params.temperature,
                                  max_tokens: params.max_tokens,
                                  tools: params.tools ? params.tools.length : 0,
                                },
                                streaming: true
                              };
                            }
                            
                            // For non-streaming, include response data
                            return {
                              model: params.model,
                              input: {
                                messages: params.messages,
                                temperature: params.temperature,
                                max_tokens: params.max_tokens,
                                tools: params.tools ? params.tools.length : 0,
                              },
                              output: result.choices?.[0]?.message,
                              metrics: result.usage ? {
                                prompt_tokens: result.usage.prompt_tokens,
                                completion_tokens: result.usage.completion_tokens,
                                total_tokens: result.usage.total_tokens,
                              } : undefined,
                              finish_reason: result.choices?.[0]?.finish_reason
                            };
                          }
                        });
                      }
                      
                      return completionsValue;
                    }
                  });
                }
                
                return chatValue;
              }
            });
          }
          
          return value;
        }
      });
    } else {
      this.client = baseClient;
    }

    this.getModels();
    
    // Set configuration options
    this.model = config.model || "llama3-8b-8192";
    this.temperature = config.temperature ?? 0.7;
    this.maxTokens = config.maxTokens;
    this.cache = config.cache ?? false;
    this.disableParallelToolCalls = config.disableParallelToolCalls ?? true;
  }

  /**
   * Send a thread to the LLM and get a response
   * @param thread Thread to send
   * @returns Updated thread with LLM response
   */
  async sendThread(thread: Thread): Promise<Thread> {
    if (thread.all().length === 0) {
      throw new Error("Cannot send an empty thread");
    }

    this.debug("Sending message to Groq API", { model: this.model });

    // Format messages for Groq API
    const messages = this.formatMessagesForAPI(thread);

    // Check for potential token issues
    const totalCharacters = messages.reduce(
      (sum, msg) => sum + (typeof msg.content === "string" ? msg.content.length : 0),
      0
    );

    if (totalCharacters > MAX_PROMPT_CHARACTERS) {
      throw new Error(`The thread content is too long (${totalCharacters} characters). Try a shorter conversation.`);
    }

    try {
      // Create parameters for the API call
      const params: any = {
        model: this.model,
        messages,
        temperature: this.temperature,
      };

      // Add max tokens if specified
      if (this.maxTokens) {
        params.max_tokens = this.maxTokens;
      }

      // Add tools if there are any in the thread
      const tools = thread.getTools();
      if (tools.length > 0) {
        params.tools = this.createToolDefinitions(tools);
        params.tool_choice = "auto";
        
        // Disable parallel tool calls if specified
        if (this.disableParallelToolCalls) {
          params.parallel_tool_calls = false;
        }
      }

      // Make the API call
      const response = await this.client.chat.completions.create(params);

      // Get the last choice from the response
      const lastChoice = response.choices[0];
      if (!lastChoice) {
        throw new Error("No response from Groq API");
      }

      const choice = lastChoice.message;
      if (!choice) {
        throw new Error("Empty response from Groq API");
      }

      // Create a new message from the response
      const message = new Message({
        role: "assistant",
        content: choice.content || "",
        toolCalls: this.parseToolCalls(choice.tool_calls),
        cache: this.cache,
      });

      // Add the message to the thread
      thread.push(message);

      // Log usage metrics if available
      if (response.usage) {
        this.debug("Groq API usage metrics", {
          inputTokens: response.usage.prompt_tokens,
          outputTokens: response.usage.completion_tokens,
          totalTokens: response.usage.total_tokens,
        });
      }

      return thread;
    } catch (error: any) {
      // Check if this is a tool call error with failed_generation information
      if (error?.error?.code === 'tool_use_failed' && error?.error?.failed_generation) {
        this.debug("Handling tool call error with failed_generation", { 
          failed_generation: error.error.failed_generation 
        });
        
        try {
          // Try to extract the tool calls from the failed generation
          const toolCalls = this.parseFailedGenerationToolCalls(error.error.failed_generation);
          if (toolCalls && toolCalls.length > 0) {
            // Create an assistant message with the tool calls
            const assistantMessage = new Message({
              role: "assistant",
              content: "I'll help you calculate that.",
              toolCalls: toolCalls,
              cache: this.cache,
            });
            
            // Add the message to the thread
            thread.push(assistantMessage);
            
            // For each tool call, create and execute the tool
            const tools = thread.getTools();
            for (const toolCall of toolCalls) {
              const tool = tools.find(t => t.getName() === toolCall.name);
              if (tool) {
                // Execute the tool
                const result = await tool.executeTool(toolCall.arguments, toolCall);
                
                // Create a user message with the tool result
                const userMessage = new Message({
                  role: "user",
                  content: "",
                  toolResults: [{
                    toolUseId: toolCall.toolUseId,
                    name: toolCall.name,
                    result: result.result,
                    error: result.error
                  }],
                  cache: this.cache,
                });
                
                // Add the message to the thread
                thread.push(userMessage);
              }
            }
            
            // Create a final message to summarize the results
            // Call ourselves recursively to generate a summary
            // But first check if we have both tool results in the thread
            const toolResults = thread.all()
              .filter(msg => msg.toolResults && msg.toolResults.length > 0)
              .flatMap(msg => msg.toolResults || []);
            
            if (toolResults.length === toolCalls.length) {
              // We have all the tool results, generate a summary
              const summaryMessage = new Message({
                role: "user",
                content: "Please provide the final answer based on the calculator results.",
                cache: this.cache,
              });
              
              thread.push(summaryMessage);
              
              // Call the LLM again to get the summary
              return this.sendThread(thread);
            }
            
            return thread;
          }
        } catch (parseError) {
          this.error("Error parsing failed_generation", { parseError, originalError: error });
        }
      }
      
      this.error("Error sending message to Groq API", { error });
      throw error;
    }
  }

  /**
   * Stream a thread to the LLM and get a streaming response
   * @param thread Thread to send
   * @returns AsyncGenerator yielding the stream and updated thread
   */
  async *streamThread(thread: Thread): AsyncGenerator<
    { stream: AsyncGenerator<string, void>; message: Message }, Thread
  > {
    if (thread.all().length === 0) {
      throw new Error("Cannot stream an empty thread");
    }

    this.debug("Streaming message to Groq API", { model: this.model });

    // Format messages for Groq API
    const messages = this.formatMessagesForAPI(thread);

    // Check for potential token issues
    const totalCharacters = messages.reduce(
      (sum, msg) => sum + (typeof msg.content === "string" ? msg.content.length : 0),
      0
    );

    if (totalCharacters > MAX_PROMPT_CHARACTERS) {
      throw new Error(`The thread content is too long (${totalCharacters} characters). Try a shorter conversation.`);
    }

    // Create a new message to add to the thread
    const message = new Message({
      role: "assistant",
      content: "",
      cache: this.cache,
    });

    try {
      // Create parameters for the API call
      const params: any = {
        model: this.model,
        messages,
        temperature: this.temperature,
        stream: true,
      };

      // Add max tokens if specified
      if (this.maxTokens) {
        params.max_tokens = this.maxTokens;
      }

      // Add tools if there are any in the thread
      const tools = thread.getTools();
      if (tools.length > 0) {
        params.tools = this.createToolDefinitions(tools);
        params.tool_choice = "auto";
        
        // Disable parallel tool calls if specified
        if (this.disableParallelToolCalls) {
          params.parallel_tool_calls = false;
        }
      }

      // Create a simple async generator for collecting content
      const contentGenerator = async function* (this: GroqThreadDriver): AsyncGenerator<string, void> {
        try {
          // Make the streaming API call
          // Note: Type assertion to any is needed because the Groq SDK types don't match reality
          const response: any = await this.client.chat.completions.create(params);
          
          // Handle streaming or direct response
          if (response && response[Symbol.asyncIterator]) {
            // It's a proper async iterator, proceed with streaming
            for await (const chunk of response) {
              const delta = chunk.choices && chunk.choices[0]?.delta;
              if (delta && delta.content) {
                // Update message content
                const updatedMessage = new Message({
                  role: "assistant",
                  content: (message.content || "") + delta.content,
                  cache: message.cache,
                });
                Object.assign(message, updatedMessage);
                
                yield delta.content;
              }
            }
          } else if (response && response.choices && response.choices[0]?.message) {
            // It's a regular completion response, not a stream
            const content = response.choices[0].message.content || "";
            
            // Update message content
            const updatedMessage = new Message({
              role: "assistant",
              content: content,
              cache: message.cache,
            });
            Object.assign(message, updatedMessage);
            
            yield content;
          }
        } catch (error: any) {
          // Check if this is a tool call error with failed_generation
          if (error?.error?.code === 'tool_use_failed' && error?.error?.failed_generation) {
            this.debug("Caught tool use failed error in streaming", { error });
            
            try {
              const toolCalls = this.parseFailedGenerationToolCalls(error.error.failed_generation);
              if (toolCalls && toolCalls.length > 0) {
                // Update message with tool calls
                const content = "I'll help you with that calculation.";
                const updatedMessage = new Message({
                  role: "assistant",
                  content: content,
                  toolCalls,
                  cache: message.cache,
                });
                Object.assign(message, updatedMessage);
                
                yield content;
              }
            } catch (parseError) {
              this.error("Error parsing failed_generation", { 
                parseError, 
                originalError: error 
              });
            }
          } else {
            this.error("Error during streaming", { error });
            throw error;
          }
        }
      }.bind(this);

      // Yield the stream and message
      yield { stream: contentGenerator(), message };

      // Add the message to the thread
      thread.push(message);

      // Return the updated thread
      return thread;
    } catch (error: any) {
      this.error("Error preparing streaming message to Groq API", { error });
      throw error;
    }
  }

  /**
   * Parse failed generation tool calls from error response
   */
  private parseFailedGenerationToolCalls(failedGeneration: string): ToolCall[] | null {
    try {
      // First, extract the JSON from the failed_generation which may be wrapped in tags
      const jsonMatch = /<tool-use>([\s\S]*?)<\/tool-use>/.exec(failedGeneration);
      
      let jsonString: string;
      if (jsonMatch && jsonMatch[1]) {
        jsonString = jsonMatch[1].trim();
      } else {
        // If no tags, try to parse the whole string
        jsonString = failedGeneration.trim();
      }
      
      // Parse the JSON
      const parsed = JSON.parse(jsonString);
      
      // Look for tool_calls in the parsed object
      if (parsed.tool_calls && Array.isArray(parsed.tool_calls)) {
        return parsed.tool_calls.map((call: any) => {
          // Get parameters from either parameters or function.parameters
          const params = call.parameters || (call.function && call.function.parameters) || {};
          
          // Get name from either name or function.name
          const name = call.function?.name || call.name;
          
          if (!name) {
            this.error("Missing tool name in failed generation", { call });
            return null;
          }
          
          return {
            toolUseId: call.id || nanoid(),
            name,
            arguments: params
          };
        }).filter(Boolean);
      }
      
      return null;
    } catch (error) {
      this.error("Error parsing failed generation", { error, failedGeneration });
      return null;
    }
  }

  /**
   * Format messages from the Thread object to the Groq API format
   */
  private formatMessagesForAPI(thread: Thread): any[] {
    const messages: any[] = [];

    for (const message of thread.all()) {
      // Process attachments if they exist
      if (message.attachments && message.attachments.length > 0) {
        const contentParts: any[] = [];
        
        // Add text content if it exists
        if (message.content) {
          contentParts.push({
            type: "text",
            text: message.content,
          });
        }
        
        // Add each attachment
        for (const attachment of message.attachments) {
          if (attachment.type === "image" && attachment.source.uri) {
            contentParts.push({
              type: "image_url",
              image_url: {
                url: attachment.source.uri,
                detail: "auto",
              },
            });
          }
        }
        
        // Groq has similar typing requirements as OpenAI for different roles
        if (message.role === "user") {
          messages.push({
            role: "user",
            content: contentParts,
          });
        } else if (message.role === "assistant") {
          messages.push({
            role: "assistant",
            content: contentParts,
          });
        } else if (message.role === "system") {
          messages.push({
            role: "system",
            content: contentParts,
          });
        }
        continue;
      }

      // Handle tool results (these are created from user messages with toolResults)
      if (message.toolResults && message.toolResults.length > 0) {
        // First add the user message if it has content
        if (message.content) {
          messages.push({
            role: "user",
            content: message.content,
          });
        }

        // Then add each tool result as a separate message
        for (const toolResult of message.toolResults) {
          const resultContent = typeof toolResult.result === 'object' 
            ? JSON.stringify(toolResult.result) 
            : String(toolResult.result);
          
          messages.push({
            role: "tool",
            tool_call_id: toolResult.toolUseId,
            content: resultContent,
          });
        }
        continue;
      }

      // Handle tool calls in assistant messages
      if (message.role === "assistant" && message.toolCalls && message.toolCalls.length > 0) {
        const toolCalls = message.toolCalls.map(call => ({
          id: call.toolUseId,
          type: "function" as const,
          function: {
            name: call.name,
            arguments: JSON.stringify(call.arguments),
          },
        }));

        messages.push({
          role: "assistant",
          content: message.content || null,
          tool_calls: toolCalls,
        });
        continue;
      }

      // Default case for simple messages
      if (message.role === "user") {
        messages.push({
          role: "user",
          content: message.content || "",
        });
      } else if (message.role === "assistant") {
        messages.push({
          role: "assistant",
          content: message.content || "",
        });
      } else if (message.role === "system") {
        messages.push({
          role: "system",
          content: message.content || "",
        });
      }
    }

    return messages;
  }

  /**
   * Create tool definitions for the Groq API
   */
  private createToolDefinitions(tools: Tool[]): any[] {
    return tools.map(tool => ({
      type: "function",
      function: {
        name: tool.getName(),
        description: tool.getDescription(),
        parameters: {
          ...zodToJsonSchema(tool.getParameters()),
          $schema: JSON_SCHEMA_URL,
        },
      },
    }));
  }

  /**
   * Parse tool calls from the Groq API response
   */
  private parseToolCalls(toolCalls?: any[]): ToolCall[] | undefined {
    if (!toolCalls || toolCalls.length === 0) {
      return undefined;
    }

    return toolCalls.map(toolCall => {
      if (toolCall.type === 'function' && toolCall.function.name) {
        try {
          const args = JSON.parse(toolCall.function.arguments || "{}");
          return {
            toolUseId: toolCall.id,
            name: toolCall.function.name,
            arguments: args,
          };
        } catch (error) {
          this.error("Error parsing tool call arguments", {
            error,
            toolCall,
          });
          return {
            toolUseId: toolCall.id,
            name: toolCall.function.name,
            arguments: {},
          };
        }
      }
      
      this.error("Unknown tool call type", { toolCall });
      return {
        toolUseId: toolCall.id || nanoid(),
        name: toolCall.function?.name || "unknown",
        arguments: {},
      };
    });
  }

  async getModels() {
    try {
      const models = await this.client.models.list();
      return models.data.map((model: any) => model.id);
    } catch (error) {
      this.error("Error getting models", { error });
      return [];
    }
  }
} 